import { cn } from "../lib/cn";

/**
 * Flat-vector people portraits for the marketing pages (hero stack,
 * testimonials, blog bylines). Drawn in-repo as SVG so they're license-free,
 * weightless (no image requests) and prerender-safe — `renderToString` only
 * sees plain markup. Known names get a hand-curated look via `PEOPLE`; unknown
 * names fall back to a deterministic seeded combination so the same name always
 * renders the same face.
 */

type HairStyle =
  | "short"
  | "crop"
  | "swoop"
  | "bob"
  | "long"
  | "bun"
  | "curls"
  | "afro"
  | "hijab"
  | "balding";

export interface PortraitSpec {
  /** Skin tone pair: fill + the slightly darker shade used for nose/shadows. */
  skin: string;
  skinShade: string;
  hair: HairStyle;
  hairColor: string;
  shirt: string;
  /** Soft background tint behind the bust. */
  bg: string;
  beard?: "full" | "stubble";
  glasses?: boolean;
}

const SKIN = {
  fair: { skin: "#F6D3B8", skinShade: "#E5B393" },
  light: { skin: "#F0C5A0", skinShade: "#DBA77D" },
  tan: { skin: "#DDA47C", skinShade: "#C68A60" },
  olive: { skin: "#C98E62", skinShade: "#B07749" },
  brown: { skin: "#A96F4E", skinShade: "#8F5839" },
  deep: { skin: "#85543A", skinShade: "#6E422A" },
} as const;

const HAIR_COLORS = {
  black: "#2E2A28",
  darkBrown: "#46342A",
  brown: "#6B4A2F",
  chestnut: "#7B4B33",
  blond: "#D2A35B",
  copper: "#A8522E",
  grey: "#8E887D",
  salt: "#B4AEA3",
} as const;

const SHIRTS = [
  "#2F4866", // navy
  "#2F6F6A", // teal
  "#3E6B4F", // forest
  "#414955", // charcoal
  "#C26D4F", // terracotta
  "#C99838", // mustard
  "#6E4A6E", // plum
  "#4D5B8C", // indigo
  "#8C4351", // burgundy
  "#6F7445", // olive
  "#4C6E8C", // steel
  "#B4583B", // rust
] as const;

const BGS = [
  "#E3EFE8", // mint
  "#F1EBDD", // sand
  "#E4EDF5", // sky
  "#F5E9E4", // blush
  "#ECEAF5", // lilac
  "#EDF0E2", // pistachio
] as const;

/**
 * Hand-curated looks for the people named on the landing page (testimonials +
 * hero stack) and the blog byline. Keys must match the i18n names exactly —
 * both locales use the same names.
 */
export const PEOPLE: Record<string, PortraitSpec> = {
  "Sanne de Vries": { ...SKIN.fair, hair: "bob", hairColor: HAIR_COLORS.chestnut, shirt: SHIRTS[1], bg: BGS[0] },
  "Thomas Bakker": { ...SKIN.light, hair: "short", hairColor: HAIR_COLORS.darkBrown, shirt: SHIRTS[0], bg: BGS[1] },
  "Yasmin El Amrani": { ...SKIN.tan, hair: "long", hairColor: HAIR_COLORS.black, shirt: SHIRTS[4], bg: BGS[2] },
  "Daan Koster": { ...SKIN.fair, hair: "swoop", hairColor: HAIR_COLORS.blond, shirt: SHIRTS[3], bg: BGS[5] },
  "Lotte Visser": { ...SKIN.fair, hair: "bun", hairColor: HAIR_COLORS.copper, shirt: SHIRTS[1], bg: BGS[3] },
  "Mehmet Yıldız": { ...SKIN.tan, hair: "short", hairColor: HAIR_COLORS.black, shirt: SHIRTS[10], bg: BGS[0], beard: "full" },
  "Iris Hendriks": { ...SKIN.light, hair: "bob", hairColor: HAIR_COLORS.black, shirt: SHIRTS[5], bg: BGS[2] },
  "Bram Mulder": { ...SKIN.fair, hair: "crop", hairColor: HAIR_COLORS.brown, shirt: SHIRTS[2], bg: BGS[1], beard: "stubble" },
  "Fatima Ouali": { ...SKIN.tan, hair: "hijab", hairColor: "#B07A8C", shirt: SHIRTS[6], bg: BGS[5] },
  "Sven Jansen": { ...SKIN.fair, hair: "crop", hairColor: HAIR_COLORS.blond, shirt: SHIRTS[3], bg: BGS[2] },
  "Anouk Smit": { ...SKIN.fair, hair: "long", hairColor: HAIR_COLORS.blond, shirt: SHIRTS[7], bg: BGS[3] },
  "Ravi Pratap": { ...SKIN.brown, hair: "swoop", hairColor: HAIR_COLORS.black, shirt: SHIRTS[8], bg: BGS[0] },
  "Marit Visscher": { ...SKIN.light, hair: "bun", hairColor: HAIR_COLORS.brown, shirt: SHIRTS[9], bg: BGS[4] },
  "Joost van Dijk": { ...SKIN.fair, hair: "balding", hairColor: HAIR_COLORS.grey, shirt: SHIRTS[0], bg: BGS[1], glasses: true },
  "Selin Demir": { ...SKIN.olive, hair: "long", hairColor: HAIR_COLORS.darkBrown, shirt: SHIRTS[11], bg: BGS[5] },
  "Pieter de Groot": { ...SKIN.fair, hair: "short", hairColor: HAIR_COLORS.salt, shirt: SHIRTS[2], bg: BGS[2], beard: "stubble" },
  "Nadia Bakir": { ...SKIN.tan, hair: "curls", hairColor: HAIR_COLORS.black, shirt: "#107652", bg: BGS[1] },
  "Erik Schouten": { ...SKIN.light, hair: "short", hairColor: HAIR_COLORS.grey, shirt: SHIRTS[10], bg: BGS[0], glasses: true },
  "Marijn Alfred": { ...SKIN.light, hair: "swoop", hairColor: HAIR_COLORS.brown, shirt: "#107652", bg: BGS[0] },
};

/** Tiny FNV-1a so unknown names map to a stable look. */
function hashName(name: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// Religion-coded looks (hijab) are never assigned by the random fallback —
// curated entries only.
const FALLBACK_HAIR: HairStyle[] = ["short", "crop", "swoop", "bob", "long", "bun", "curls", "balding"];
const SKIN_LIST = Object.values(SKIN);
const HAIR_COLOR_LIST = Object.values(HAIR_COLORS);

export function portraitFor(name: string): PortraitSpec {
  const known = PEOPLE[name];
  if (known) return known;
  const h = hashName(name);
  const hair = FALLBACK_HAIR[h % FALLBACK_HAIR.length]!;
  const beardable = hair === "short" || hair === "crop" || hair === "swoop" || hair === "balding";
  return {
    ...SKIN_LIST[(h >>> 3) % SKIN_LIST.length]!,
    hair,
    hairColor: HAIR_COLOR_LIST[(h >>> 7) % HAIR_COLOR_LIST.length]!,
    shirt: SHIRTS[(h >>> 11) % SHIRTS.length]!,
    bg: BGS[(h >>> 17) % BGS.length]!,
    beard: beardable && (h >>> 21) % 3 === 0 ? "stubble" : undefined,
    glasses: (h >>> 24) % 5 === 0,
  };
}

/* ── Geometry (viewBox 0 0 96 96; head ellipse 48,40 r 15.5×17) ─────────── */

const HAIR_FRONT: Partial<Record<HairStyle, string>> = {
  short:
    "M32.2 42 C32.2 26.5 38.5 21.5 48 21.5 C57.5 21.5 63.8 26.5 63.8 42 C63.8 42 62.8 33.4 59.6 30.8 C55.4 27.4 40.6 27.4 36.4 30.8 C33.2 33.4 32.2 42 32.2 42 Z",
  crop:
    "M32.4 40 C32.4 26.4 38.6 21.8 48 21.8 C57.4 21.8 63.6 26.4 63.6 40 C63.6 40 62 31.2 58.8 29.2 C54.6 26.6 41.4 26.6 37.2 29.2 C34 31.2 32.4 40 32.4 40 Z",
  swoop:
    "M32 42 C31.6 26 38.5 21 48.5 21.2 C58 21.4 64 26.8 63.9 42 C63.9 42 63 32.2 60.2 30.2 C57.8 33.6 49.5 35.2 42.5 33.6 C37.8 32.5 35.6 30.2 35.3 28.4 C33.4 31.2 32 42 32 42 Z",
  bob:
    "M30.8 50 C29.6 27.5 37 20.4 48 20.4 C59 20.4 66.4 27.5 65.2 50 C64.8 53.4 62.6 55.6 60.4 55.8 C61.4 49 61.3 40 59.8 33.4 C54.6 36.6 41.4 36.2 36.2 32.8 C34.7 39.8 34.6 49 35.6 55.8 C33.4 55.6 31.2 53.4 30.8 50 Z",
  long:
    "M32.2 44 C31.8 25.5 39 20.8 48 20.8 C57 20.8 64.2 25.5 63.8 44 C63.8 44 63 30.8 59.8 29 C57.4 31.6 52.8 32.8 48 32.8 C43.2 32.8 38.6 31.6 36.2 29 C33 30.8 32.2 44 32.2 44 Z",
  bun:
    "M32.4 40 C32.4 26.4 38.6 21.8 48 21.8 C57.4 21.8 63.6 26.4 63.6 40 C63.6 40 62 31.2 58.8 29.2 C54.6 26.6 41.4 26.6 37.2 29.2 C34 31.2 32.4 40 32.4 40 Z",
  curls:
    "M32.4 40 C32.4 26.4 38.6 21.8 48 21.8 C57.4 21.8 63.6 26.4 63.6 40 C63.6 40 62 31.2 58.8 29.2 C54.6 26.6 41.4 26.6 37.2 29.2 C34 31.2 32.4 40 32.4 40 Z",
  afro:
    "M32.4 40 C32.4 26.4 38.6 21.8 48 21.8 C57.4 21.8 63.6 26.4 63.6 40 C63.6 40 62 31.2 58.8 29.2 C54.6 26.6 41.4 26.6 37.2 29.2 C34 31.2 32.4 40 32.4 40 Z",
};

const BALDING_LEFT = "M32.1 37.5 C31.8 41.5 32.6 45 34.4 47.2 C35.2 44 35.3 40 34.8 36.6 Z";
const BALDING_RIGHT = "M63.9 37.5 C64.2 41.5 63.4 45 61.6 47.2 C60.8 44 60.7 40 61.2 36.6 Z";

const LONG_BACK =
  "M28 86 C24.5 60 26.5 36 33.5 27 C39 19.8 57 19.8 62.5 27 C69.5 36 71.5 60 68 86 C56.5 90 39.5 90 28 86 Z";

const BEARD =
  "M32.9 40.5 C32.9 51 39 59.8 48 59.8 C57 59.8 63.1 51 63.1 40.5 C63.1 40.5 60.5 45.4 55.8 46.4 C50.8 47.5 45.2 47.5 40.2 46.4 C35.5 45.4 32.9 40.5 32.9 40.5 Z";

const HIJAB_OUTER =
  "M48 17.5 C35 17.5 28.5 27 28.5 41 C28.5 50 31 56.5 35 60 L35 70 C39.5 67 44 65.5 48 65.5 C52 65.5 56.5 67 61 70 L61 60 C65 56.5 67.5 50 67.5 41 C67.5 27 61 17.5 48 17.5 Z";

const CURL_BLOBS: [number, number, number][] = [
  [37, 26.5, 6.5],
  [42, 23, 6],
  [48, 22, 7],
  [54, 23, 6],
  [59, 26.5, 6.5],
];

export function FacePortrait({
  name,
  size = 40,
  shape = "circle",
  className,
}: {
  /** Person's display name — picks the curated look or seeds the fallback. */
  name: string;
  size?: number;
  /** Marketing surfaces use circles; the blog's Rijksstijl chrome uses squares. */
  shape?: "circle" | "square" | "rounded";
  className?: string;
}) {
  const p = portraitFor(name);
  const hijab = p.hair === "hijab";
  const features = (
    <g>
      {/* brows (under a fringe when the hair dips this low — intentional) */}
      <path
        d="M39.2 37.4 Q42 36 44.8 37.2 M51.2 37.2 Q54 36 56.8 37.4"
        stroke={p.hair === "balding" ? "#6B6257" : p.hairColor}
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
        opacity="0.85"
      />
      {/* eyes */}
      <circle cx="42.2" cy="41.6" r="1.8" fill="#332E2B" />
      <circle cx="53.8" cy="41.6" r="1.8" fill="#332E2B" />
      {/* nose */}
      <path
        d="M48 42.6 Q49.1 44.7 47.7 45.9"
        stroke={p.skinShade}
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* smile */}
      <path
        d="M44.2 49 Q48 51.8 51.8 49"
        stroke="#9C5243"
        strokeWidth="1.8"
        strokeLinecap="round"
        fill="none"
      />
      {/* blush (a full beard covers the cheeks) */}
      {p.beard !== "full" && (
        <>
          <ellipse cx="38.8" cy="46" rx="2.5" ry="1.5" fill="#D9776A" opacity="0.28" />
          <ellipse cx="57.2" cy="46" rx="2.5" ry="1.5" fill="#D9776A" opacity="0.28" />
        </>
      )}
      {p.glasses && (
        <g stroke="#3D3A37" strokeWidth="1.5" fill="none" opacity="0.9">
          <circle cx="42.2" cy="41.6" r="4.8" />
          <circle cx="53.8" cy="41.6" r="4.8" />
          <path d="M47 41.2 L49 41.2 M37.4 40.8 L32.8 39.8 M58.6 40.8 L63.2 39.8" />
        </g>
      )}
    </g>
  );

  return (
    <span
      className={cn(
        "inline-flex shrink-0 select-none overflow-hidden",
        shape === "circle" && "rounded-full",
        shape === "rounded" && "rounded-lg",
        className,
      )}
      style={{ width: size, height: size }}
      title={name}
    >
      <svg
        data-portrait={name}
        viewBox="0 0 96 96"
        width={size}
        height={size}
        role="img"
        aria-label={name}
        className="block h-full w-full"
      >
        <rect width="96" height="96" fill={p.bg} />
        {p.hair === "long" && <path d={LONG_BACK} fill={p.hairColor} />}
        {p.hair === "afro" && <circle cx="48" cy="29" r="20" fill={p.hairColor} />}
        {/* shoulders / shirt */}
        <path d="M16 96 C16 77 29 67.5 48 67.5 C67 67.5 80 77 80 96 Z" fill={p.shirt} />
        {!hijab && (
          <>
            <rect x="42.5" y="50" width="11" height="18" rx="4.5" fill={p.skin} />
            <ellipse cx="48" cy="58.5" rx="5.5" ry="3" fill="#000" opacity="0.10" />
            <circle cx="32.5" cy="42" r="3.2" fill={p.skin} />
            <circle cx="63.5" cy="42" r="3.2" fill={p.skin} />
          </>
        )}
        <ellipse cx="48" cy="40" rx="15.5" ry="17" fill={p.skin} />
        {hijab ? (
          <>
            <path d={HIJAB_OUTER} fill={p.hairColor} />
            <ellipse cx="48" cy="41.5" rx="11.5" ry="13" fill={p.skin} />
          </>
        ) : (
          <>
            {p.hair === "balding" && (
              <g fill={p.hairColor}>
                <path d={BALDING_LEFT} />
                <path d={BALDING_RIGHT} />
              </g>
            )}
            {p.hair === "curls" &&
              CURL_BLOBS.map(([cx, cy, r]) => <circle key={`${cx}`} cx={cx} cy={cy} r={r} fill={p.hairColor} />)}
            {HAIR_FRONT[p.hair] && <path d={HAIR_FRONT[p.hair]} fill={p.hairColor} />}
            {p.hair === "bun" && <ellipse cx="48" cy="17" rx="7" ry="6" fill={p.hairColor} />}
            {p.beard && <path d={BEARD} fill={p.hairColor} opacity={p.beard === "stubble" ? 0.4 : 1} />}
          </>
        )}
        {features}
      </svg>
    </span>
  );
}
